import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormArray, UntypedFormControl } from '@angular/forms';
import { Store } from '@ngrx/store';
import { finalize } from 'rxjs/operators';

import { TagCategoryInfoFragment } from 'generated/graphql';

import {
  ENTITY_CONNECTIONS_CHANGED
} from 'app/_state/entity/view-entity/entity-connections/entity-connection.actions';
import { reportHandledError } from 'core/report-handled-error';
import { ToastrService } from 'core/toastr.service';
import {
  ApplyTagsService, ApplyTagsServiceInput, OnTagAppliedResponse
} from 'field-shared/apply-tags.service';
import { ApplyTagFormModel } from 'field-shared/ApplyTagFormModel';

/**
 * Component for adding tag(s) to an entity
 * requires the category to passed to the input
 */
@Component({
  selector: 'app-apply-tag',
  templateUrl: './apply-tag.component.html'
})
export class ApplyTagComponent implements OnInit {
  @Input() tagCategory: TagCategoryInfoFragment;
  @Input() campaignId: string;
  @Input() targetType: 'Entity' | 'EntityConnection' | 'CalendarEventResponse';
  /** Entity Id or EntityConnection id */
  @Input() targetId: string;

  @Output() changesSaved = new EventEmitter<any>();

  isSaving = false;

  errors: OnTagAppliedResponse[] = [];

  tagsFormArray = new UntypedFormArray([]);

  get tagsForArrayValues(): ApplyTagFormModel[] {
    return this.tagsFormArray.value;
  }

  // 322
  /**
   *
   */
  constructor(
    private _applyTagsService: ApplyTagsService,
    private _toastrService: ToastrService,
    private _store: Store
  ) {
    // super();
  }

  ngOnInit() {
    if (!this.targetType) {
      console.warn('TargetType input not set');
    }
    if (!this.tagCategory.multiselectable) {
      this.tagsFormArray.push(new UntypedFormControl());
    }
  }

  onSaveClick() {
    this._applyTags();
  }

  private _applyTags() {
    if (this.isSaving) {
      return;
    }
    this.isSaving = true;

    this.errors = [];

    const applyTagInput: ApplyTagsServiceInput = {
      targetInfo: {
        targetId: this.targetId,
        targetType: this.targetType,
        campaignId: this.campaignId
      },
      tags: this.tagsForArrayValues
    };

    this._applyTagsService.applyTags(
      applyTagInput, this.onAppliedCallback.bind(this)
    ).pipe(
      finalize(() => this.isSaving = false)
    )
      .subscribe(
        (res) => {
          if (this.targetType === 'EntityConnection') {
            this._store.dispatch(ENTITY_CONNECTIONS_CHANGED({ payload: { entityConnection: (res.target as any) } }));
          }
          if (res.errors.length > 0) {
            // The service already reported each failure to Sentry with the
            // underlying error, so skip logging the generic message again.
            this._toastrService.errorSkipLog('An error occured applying responses.');
            this.errors = res.errors;
          } else {
            this._toastrService.success('Responses saved');

          }
        },
        (err) => {
          // Reached when the refetch of the target fails; the per-tag failures
          // are caught in the service and arrive on `res.errors` instead.
          reportHandledError(err, {
            operation: 'applyTag.refetchTarget',
            tags: { target_type: this.targetType },
            extra: {
              target_id: this.targetId,
              campaign_id: this.campaignId,
              tag_count: this.tagsForArrayValues.length
            }
          });
          this._toastrService.errorSkipLog('An error occured applying responses.');
        },
        () => {
          this.changesSaved.emit(true);
        }
      );
  }

  private onAppliedCallback(onAppliedResponse: OnTagAppliedResponse) {
    if (!onAppliedResponse.error && this.tagCategory.multiselectable) {
      // Remove successfully applied tags
      const idx = this.tagsForArrayValues
        .findIndex(t => t.selectedTag?.id === onAppliedResponse.mutationInput.tagId);
      this.tagsFormArray.removeAt(idx);
    }
  }
}
